import gql from "graphql-tag";

const SchemaReferenceFragment = gql`
  fragment SchemaReference on SchemaReference {
    __typename
    name
    namespace
    version
  }
`;

const SubjectBasicFragment = gql`
  fragment SubjectBasic on Subject {
    id
    name
    namespace
    description
    version
  }
`;

const SubjectFullFragment = gql`
  fragment SubjectFullFields on Subject {
    ...SubjectBasic
    __typename
    type

    canUserEdit
    properties {
      __typename
      id
      schema {
        ...SchemaReference
      }
      properties {
        id
        name
        namespace
        description
        expectedType {
          ...SchemaReference
        }
      }
    }

    subTypeOf {
      ...SubjectBasic
    }

    ancestors {
      ...SubjectBasic
    }

    children {
      ...SubjectBasic
    }

    updatedAt
    license {
      id
      name
      description
      url
      default
      nonCore
      logo
    }

    owner {
      __typename
      ... on User {
        id
        fullName
        shortname
      }
      ... on Org {
        id
        name
        shortname
      }
    }
  }
  ${SubjectBasicFragment}
  ${SchemaReferenceFragment}
`;

export const subjectsQuery = gql`
  query getSubjects($onlyPopulated: [ProjectTypeFilter!]) {
    subjects(onlyPopulated: $onlyPopulated) {
      id
      name
      namespace
      version
      description
      parentChain
    }
  }
`;

export const propertiesQuery = gql`
  query getProperties {
    properties {
      id
      description
      name
      namespace
      version
    }
  }
`;

export const subjectQuery = gql`
  query subject($name: String!, $namespace: String, $version: String) {
    subject(name: $name, namespace: $namespace, version: $version) {
      ...SubjectFullFields
    }
  }
  ${SubjectBasicFragment}
  ${SubjectFullFragment}
  ${SchemaReferenceFragment}
`;

export const subjectDescriptionQuery = gql`
  query subjectDescription(
    $name: String!
    $namespace: String
    $version: String
  ) {
    subject(name: $name, namespace: $namespace, version: $version) {
      id
      name
      description
    }
  }
`;

const PropertyFieldsFragment = gql`
  fragment PropertyFields on Property {
    __typename
    id
    canUserEdit
    name
    namespace
    description
    expectedType {
      ...SchemaReference
    }
    inverseOf {
      ...SchemaReference
    }
    owner {
      __typename
      ... on User {
        id
        fullName
        shortname
      }
      ... on Org {
        id
        name
        shortname
      }
    }
    propertyOf {
      id
      name
      namespace
    }
    subPropertyOf {
      ...SchemaReference
    }
    subProperties {
      id
      name
      namespace
    }
    supersededBy {
      ...SchemaReference
    }
    createdAt
    updatedAt
  }
`;

export const propertyQuery = gql`
  query property($name: String!, $namespace: String, $version: String) {
    property(name: $name, namespace: $namespace, version: $version) {
      ...PropertyFields
    }
  }
  ${PropertyFieldsFragment}
  ${SchemaReferenceFragment}
`;

export const propertyDescriptionQuery = gql`
  query propertyDescription(
    $name: String!
    $namespace: String
    $version: String
  ) {
    property(name: $name, namespace: $namespace, version: $version) {
      id
      name
      namespace
      description
    }
  }
`;

export const createSubjectMutation = gql`
  mutation createSubject(
    $name: String!
    $namespace: String!
    $description: String!
    $subTypeOf: [SchemaReferenceInput!]
  ) {
    createSubject(
      name: $name
      namespace: $namespace
      description: $description
      subTypeOf: $subTypeOf
    ) {
      id
      name
      namespace
    }
  }
`;

export const forkSubjectMutation = gql`
  mutation forkSubject(
    $name: String!
    $namespace: String!
    $version: String
    $targetNamespace: String!
  ) {
    forkSubject(
      name: $name
      namespace: $namespace
      version: $version
      targetNamespace: $targetNamespace
    ) {
      id
      name
      namespace
    }
  }
`;

export const updateSubjectMutation = gql`
  mutation updateSubject(
    $description: String
    $name: String!
    $namespace: String!
    $properties: [SchemaReferenceInput!]
    $subTypeOf: [SchemaReferenceInput!]
    $version: String
  ) {
    updateSubject(
      description: $description
      name: $name
      namespace: $namespace
      properties: $properties
      subTypeOf: $subTypeOf
      version: $version
    ) {
      ...SubjectFullFields
    }
  }
  ${SubjectFullFragment}
`;

export const createPropertyMutation = gql`
  mutation createProperty(
    $name: String!
    $namespace: String!
    $description: String!
    $expectedType: [SchemaReferenceInput!]!
  ) {
    createProperty(
      name: $name
      namespace: $namespace
      description: $description
      expectedType: $expectedType
    ) {
      id
      name
      namespace
    }
  }
`;
